import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProject } from "../../context/ProjectContext";
import { useAllProjects } from "../../context/AllProjectsContext";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
    Checkbox,
    FormControlLabel,
    Switch,
    Alert,
    AlertTitle,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNotification } from "../../context/NotificationContext";
import ProjectService from "../../services/ProjectService";
import {
    validateProjectName,
    validateProjectURL,
    validateLoginPageURL,
} from "../../utils/validateForm";
import { isExcelhireUrl } from "../../utils/excelhireAuth";

const CreateProjectModal = ({
    open,
    setOpen,
    projectName,
    setProjectName,
    projectURL,
    setProjectURL,
    projectsToggler,
    setProjectsToggler,
    isExistedProjects,
}) => {
    const navigate = useNavigate();
    const showNotification = useNotification();
    const { setSelectedProject } = useProject();
    const { refreshProjects } = useAllProjects();

    const [createProjectLoading, setCreateProjectLoading] = useState(false);
    const [projectNameError, setProjectNameError] = useState("");
    const [projectURLError, setProjectURLError] = useState("");
    const [activeStep, setActiveStep] = useState(0);
    const [selectedResolutions, setSelectedResolutions] = useState(["desktop"]);
    const [loginRequired, setLoginRequired] = useState(false);
    const [loginPageUrl, setLoginPageUrl] = useState("");
    const [loginPageURLError, setLoginPageURLError] = useState("");
    const [login, setLogin] = useState("");
    const [organisation, setOrganisation] = useState("");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [organisationError, setOrganisationError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isOTPBased, setIsOTPBased] = useState(false);
    const [isFirebaseLogin, setIsFirebaseLogin] = useState(false);
    // NEW: default checked for prod, unchecked for sandbox
    const isSandbox = (process.env.REACT_APP_SERVER_URL || "").includes(
        "sandbox"
    );
    const [autoGenerateTests, setAutoGenerateTests] = useState(!isSandbox);

    const steps = ["URL", "Name", "Settings"];
    const isExcelhireProject =
        isExcelhireUrl(projectURL) || isExcelhireUrl(loginPageUrl);

    const handleClose = () => {
        setOpen(false);
        setActiveStep(0);
        setProjectURL("");
        setProjectName("");
        setProjectURLError("");
        setSelectedResolutions(["desktop"]);
        setLoginRequired(false);
        setLoginPageUrl("");
        setLoginPageURLError("");
        setLogin("");
        setOrganisation("");
        setPassword("");
        setLoginError("");
        setOrganisationError("");
        setShowPassword(false);
        setIsOTPBased(false);
        setIsFirebaseLogin(false);
        setAutoGenerateTests(!isSandbox);
    };

    const handleBack = (step) => {
        setActiveStep(step - 1);
    };

    const handleNext = async () => {
        if (activeStep === 0) {
            if (!validateProjectURL(projectURL)) {
                setProjectURLError("Invalid URL.");
                return;
            }

            const domain = projectURL
                .replace(/(^\w+:|^)\/\//, "")
                .replace(/^www\./, "")
                .split("/")[0];
            const formattedName =
                domain.charAt(0).toUpperCase() + domain.slice(1);
            setProjectName(formattedName);
            setActiveStep(1);
        } else if (activeStep === 1) {
            if (!validateProjectName(projectName)) {
                setProjectNameError("Project name is required.");
                return;
            }

            setActiveStep(2);
        } else {
            if (loginRequired) {
                if (!validateLoginPageURL(loginPageUrl)) {
                    setLoginPageURLError("Invalid login page URL.");
                    return;
                }

                if (!login.trim()) {
                    setLoginError("Login is required.");
                    return;
                }

                if (isExcelhireProject && !organisation.trim()) {
                    setOrganisationError("Organisation is required.");
                    return;
                }
            }

            try {
                setCreateProjectLoading(true);

                const newProject = await ProjectService.createProject(
                    projectName,
                    projectURL,
                    selectedResolutions,
                    loginRequired,
                    loginRequired ? loginPageUrl : "",
                    loginRequired ? login : "",
                    loginRequired && password.trim() ? password : "",
                    loginRequired && isExcelhireProject
                        ? organisation.trim()
                        : "",
                    loginRequired ? isOTPBased : "",
                    loginRequired ? isFirebaseLogin : "",
                    autoGenerateTests 
                );

                await refreshProjects();
                setSelectedProject(newProject);

                if (loginRequired) {
                    showNotification(
                        "Login Elements Processing has started",
                        "info",
                        "Learn more",
                        navigate
                    );
                } else {
                    showNotification(
                        "Website Mapping has started",
                        "info",
                        "Learn more",
                        navigate
                    );
                }

                if (loginRequired) {
                    navigate(`/project/${newProject.id}`, {
                        state: { activeTab: "2" },
                    });
                }

                setCreateProjectLoading(false);
                handleClose();
                setProjectsToggler(!projectsToggler);
            } catch (error) {
                setCreateProjectLoading(false);
                console.error("Error creating project:", error);
            }
        }
    };

    const handleProjectNameChange = (e) => {
        setProjectName(e.target.value.trim());
        if (projectNameError) {
            setProjectNameError("");
        }
    };

    const handleProjectURLChange = (e) => {
        setProjectURL(e.target.value.trim());
        if (projectURLError) {
            setProjectURLError("");
        }
    };

    const handleResolutionChange = (resolution) => {
        setSelectedResolutions((prev) =>
            prev.includes(resolution)
                ? prev.filter((res) => res !== resolution)
                : [...prev, resolution]
        );
    };

    const handleLoginPageURLChange = (e) => {
        setLoginPageUrl(e.target.value.trim());
        if (loginPageURLError) {
            setLoginPageURLError("");
        }
    };

    const handleLoginChange = (e) => {
        setLogin(e.target.value.trim());
        if (loginError) {
            setLoginError("");
        }
    };

    const handleOrganisationChange = (e) => {
        setOrganisation(e.target.value);
        if (organisationError) {
            setOrganisationError("");
        }
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value.trim());
    };

    const handleIsOTPBasedChange = () => {
        setIsOTPBased((prev) => !prev);
    };

    const handleIsFirebaseLoginChange = () => {
        setIsFirebaseLogin((prev) => !prev);
    };

    const handleClickShowPassword = () => {
        setShowPassword((prev) => !prev);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <Box className="createProjectModal">
                <DialogTitle className="createProjectModalTitle">
                    Create New Project
                </DialogTitle>
                <Stepper
                    className="createProjectModalStepper"
                    activeStep={activeStep}
                >
                    {steps.map((label, index) => (
                        <Step key={index}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <DialogContent className="createProjectModalContent">
                    {!isExistedProjects ? (
                        <Typography variant="body1" mb={1}>
                            You don&apos;t have any projects yet. Add your first
                            project to start exploring and improving the quality
                            of your solution.
                        </Typography>
                    ) : null}
                    {activeStep === 0 && (
                        <>
                            <Typography variant="body1" mb={2}>
                                Please enter the URL of your project
                            </Typography>
                            <TextField
                                label="Project URL"
                                autoFocus
                                type="url"
                                fullWidth
                                variant="outlined"
                                value={projectURL}
                                onChange={handleProjectURLChange}
                                required
                                error={!!projectURLError}
                                helperText={projectURLError + " "}
                            />
                        </>
                    )}
                    {activeStep === 1 && (
                        <>
                            <Typography variant="body1" mb={2}>
                                Please confirm or edit the project name
                            </Typography>
                            <TextField
                                autoFocus
                                label="Project Name"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={projectName}
                                onChange={handleProjectNameChange}
                                required
                                error={!!projectNameError}
                                helperText={projectNameError + " "}
                            />
                        </>
                    )}
                    {activeStep === 2 && (
                        <>
                            <Typography variant="body1" mb={2}>
                                You can now configure additional settings for
                                your project
                            </Typography>
                            <Box className="createProjectModalResolutionsContainer">
                                <Typography className="createProjectModalResolutionsTitle">
                                    Select Resolutions
                                </Typography>
                                <Box className="createProjectModalResolutionsOptions">
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                disabled
                                                checked={selectedResolutions.includes(
                                                    "desktop"
                                                )}
                                                onChange={() =>
                                                    handleResolutionChange(
                                                        "desktop"
                                                    )
                                                }
                                            />
                                        }
                                        label={
                                            <span style={{ fontSize: "16px" }}>
                                                Desktop
                                            </span>
                                        }
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                disabled
                                                checked={selectedResolutions.includes(
                                                    "tablet"
                                                )}
                                                onChange={() =>
                                                    handleResolutionChange(
                                                        "tablet"
                                                    )
                                                }
                                            />
                                        }
                                        label={
                                            <span style={{ fontSize: "16px" }}>
                                                Tablet
                                            </span>
                                        }
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                disabled
                                                checked={selectedResolutions.includes(
                                                    "mobile"
                                                )}
                                                onChange={() =>
                                                    handleResolutionChange(
                                                        "mobile"
                                                    )
                                                }
                                            />
                                        }
                                        label={
                                            <span style={{ fontSize: "16px" }}>
                                                Mobile
                                            </span>
                                        }
                                    />
                                </Box>
                            </Box>
                            {/* NEW: Auto-generate tests toggle */}
                            <Box className="createProjectModalAutoGenerateContainer">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={autoGenerateTests}
                                            onChange={(e) =>
                                                setAutoGenerateTests(
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    }
                                    label={
                                        <span style={{ fontSize: "16px" }}>
                                            Auto-generate and run test cases
                                        </span>
                                    }
                                />
                            </Box>
                            <Box className="createProjectModalLoginRequiredContainer">
                                <FormControlLabel
                                    className="createProjectModalSwitch"
                                    control={
                                        <Switch
                                            checked={loginRequired}
                                            onChange={(e) =>
                                                setLoginRequired(
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    }
                                    label=""
                                />
                                <Typography
                                    variant="body1"
                                    sx={{ mt: "2px", mr: "6px" }}
                                >
                                    Login Required for Scan
                                </Typography>
                            </Box>
                            {loginRequired ? (
                                <>
                                    <Alert
                                        className="createProjectModalAlert"
                                        severity="info"
                                    >
                                        <AlertTitle className="createProjectModalAlertTitle">
                                            Important
                                        </AlertTitle>
                                        <span>
                                            The login form must have its own{" "}
                                            <strong>unique URL</strong> for the
                                            scanning process to work and{" "}
                                            <strong>without Captcha</strong>
                                        </span>
                                    </Alert>
                                    <TextField
                                        label="Login page URL"
                                        type="url"
                                        fullWidth
                                        variant="outlined"
                                        value={loginPageUrl}
                                        onChange={handleLoginPageURLChange}
                                        required
                                        error={!!loginPageURLError}
                                        helperText={loginPageURLError + " "}
                                    />
                                    <TextField
                                        label="Login"
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        value={login}
                                        onChange={handleLoginChange}
                                        required
                                        autoComplete="new-login"
                                        error={!!loginError}
                                        helperText={loginError + " "}
                                    />
                                    {isExcelhireProject ? (
                                        <TextField
                                            label="Organisation"
                                            type="text"
                                            fullWidth
                                            variant="outlined"
                                            value={organisation}
                                            onChange={handleOrganisationChange}
                                            required
                                            error={!!organisationError}
                                            helperText={organisationError + " "}
                                        />
                                    ) : null}
                                    <TextField
                                        label="Password (optional)"
                                        type={
                                            showPassword ? "text" : "password"
                                        }
                                        fullWidth
                                        variant="outlined"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        autoComplete="new-password"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={
                                                            handleClickShowPassword
                                                        }
                                                        onMouseDown={
                                                            handleMouseDownPassword
                                                        }
                                                        edge="end"
                                                        sx={{ mr: -0.5 }}
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityOff />
                                                        ) : (
                                                            <Visibility />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ mb: 3 }}
                                    />
                                    <Box className="createProjectModalOTPCheckboxContainer">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    className="createProjectModalOTPCheckbox"
                                                    checked={isOTPBased}
                                                    onChange={
                                                        handleIsOTPBasedChange
                                                    }
                                                />
                                            }
                                            label={
                                                <span
                                                    style={{ fontSize: "16px" }}
                                                >
                                                    Login is based on OTP code
                                                </span>
                                            }
                                        />
                                        <FormControlLabel
                                            sx={{ ml: 2 }}
                                            control={
                                                <Checkbox
                                                    className="createProjectModalFirebaseCheckbox"
                                                    checked={isFirebaseLogin}
                                                    onChange={
                                                        handleIsFirebaseLoginChange
                                                    }
                                                />
                                            }
                                            label={
                                                <span
                                                    style={{ fontSize: "16px" }}
                                                >
                                                    Firebase login
                                                </span>
                                            }
                                        />
                                    </Box>
                                </>
                            ) : null}
                        </>
                    )}
                </DialogContent>
                <DialogActions className="createProjectModalActions">
                    <Button
                        className="createProjectModalButton"
                        onClick={
                            activeStep === 0
                                ? handleClose
                                : () => handleBack(activeStep)
                        }
                        variant="outlined"
                        color="secondary"
                    >
                        {activeStep === 0 ? "Cancel" : "Back"}
                    </Button>
                    {createProjectLoading ? (
                        <LoadingButton
                            loading
                            className="createProjectModalButton"
                            variant="contained"
                            color="secondary"
                            loadingIndicator={
                                <CircularProgress color="white" size={16} />
                            }
                        >
                            {activeStep === steps.length - 1 ? "Start" : "Next"}
                        </LoadingButton>
                    ) : (
                        <Button
                            className="createProjectModalButton"
                            onClick={handleNext}
                            variant="contained"
                            color="secondary"
                        >
                            {activeStep === steps.length - 1 ? "Start" : "Next"}
                        </Button>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default CreateProjectModal;
